/**
 * Equipo (plan Equipo, docs/equipo.md). En la cuenta personal: crear un equipo
 * y ver los equipos de los que eres parte. En un equipo: miembros, roles,
 * invitaciones, asientos y el pago del plan.
 */
import { useState } from 'react'
import { PLANS, formatMinutes } from '@eco/shared'
import { useApp, useToast, errorText } from '../components/AppContext'
import { ErrorBox, Modal, Skeleton } from '../components/ui'
import { formatDate } from '../lib/format'
import { useAsync, useDocumentTitle } from '../lib/hooks'
import { PADDLE_CLIENT_TOKEN, SITIO_URL, openPaddleCheckout } from '../lib/thirdparty'

const ROL: Record<string, string> = { owner: 'Dueño', admin: 'Administrador', member: 'Miembro' }
const clp = (n: number) => `$${n.toLocaleString('es-CL')}`

export function Team() {
  const { org } = useApp()
  useDocumentTitle('Equipo')
  return org?.tipo === 'equipo' ? <TeamAdmin /> : <CreateTeam />
}

function CreateTeam() {
  const { api, me, switchOrg } = useApp()
  const toast = useToast()
  const [nombre, setNombre] = useState('')
  const [busy, setBusy] = useState(false)
  const equipos = (me?.orgs ?? []).filter((o) => o.tipo === 'equipo')

  async function create() {
    if (busy || nombre.trim().length < 2) return
    setBusy(true)
    try {
      const { orgId } = await api.createTeam(nombre.trim())
      // Recarga la página ya dentro del equipo, para invitar
      switchOrg(orgId, '/equipo')
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="page narrow">
      <div className="page-head">
        <div className="grow">
          <h1>Equipo</h1>
          <p className="muted">
            Un espacio compartido para las reuniones de tu equipo: cada persona graba con su cuenta,
            decide qué compartir y el equipo paga en una sola factura.
          </p>
        </div>
      </div>
      <div className="stack">
        {equipos.length > 0 && (
          <section className="card stack">
            <h2>Tus equipos</h2>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {equipos.map((o) => (
                <li key={o.orgId}>
                  <button className="link" onClick={() => switchOrg(o.orgId)}>
                    {o.nombre}
                  </button>{' '}
                  <span className="faint">· {ROL[o.rol] ?? o.rol}</span>
                </li>
              ))}
            </ul>
          </section>
        )}
        <section className="card stack">
          <h2>Crear un equipo</h2>
          <form
            className="row wrap"
            onSubmit={(e) => {
              e.preventDefault()
              void create()
            }}
          >
            <input
              className="grow"
              aria-label="Nombre del equipo"
              placeholder="Nombre del equipo (p. ej. Ventas Acme)"
              value={nombre}
              maxLength={80}
              onChange={(e) => setNombre(e.target.value)}
            />
            <button className="btn primary" disabled={busy || nombre.trim().length < 2}>
              {busy ? 'Creando…' : 'Crear equipo'}
            </button>
          </form>
          <p className="faint" style={{ fontSize: 12, margin: 0 }}>
            Tus reuniones personales siguen en tu cuenta personal. En el equipo, cada reunión es
            privada de quien la grabó hasta que la comparta. Pruébalo gratis con hasta 3 personas;
            después, {clp(PLANS.equipo.precioClpMes ?? 0)} por persona al mes (mínimo 2) con{' '}
            {formatMinutes(PLANS.equipo.minutosIncluidos)} por persona para todo el equipo.
          </p>
        </section>
      </div>
    </div>
  )
}

function TeamAdmin() {
  const { api, me, switchOrg, usage, refreshUsage } = useApp()
  const toast = useToast()
  const team = useAsync(() => api.team(), [api])
  const t = team.data
  const [email, setEmail] = useState('')
  const [rol, setRol] = useState<'member' | 'admin'>('member')
  const [busy, setBusy] = useState(false)
  const [leaving, setLeaving] = useState(false)
  const [asientos, setAsientos] = useState(2)

  if (team.error)
    return (
      <div className="page narrow">
        <ErrorBox error={team.error} onRetry={() => void team.reload()} />
      </div>
    )
  if (!t)
    return (
      <div className="page narrow stack">
        <Skeleton h={34} w="40%" />
        <Skeleton h={200} />
      </div>
    )

  const admin = t.miRol === 'owner' || t.miRol === 'admin'
  const ocupados = t.miembros.length + t.invitaciones.length
  const lleno = ocupados >= t.asientos

  async function act(fn: () => Promise<unknown>, ok: string) {
    setBusy(true)
    try {
      await fn()
      toast(ok)
      await team.reload()
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setBusy(false)
    }
  }

  async function invite() {
    const correo = email.trim()
    if (!correo) return
    await act(() => api.invite(correo, rol), `Invitación enviada a ${correo}`)
    setEmail('')
  }

  async function leave() {
    setBusy(true)
    try {
      await api.removeMember(me!.user.id)
      const personal = me?.orgs.find((o) => o.tipo === 'personal')
      if (personal) switchOrg(personal.orgId)
    } catch (e) {
      toast(errorText(e), 'error')
      setBusy(false)
    }
  }

  async function pay() {
    setBusy(true)
    try {
      const r = await api.checkout('equipo', 'plan', Math.max(2, asientos))
      if (PADDLE_CLIENT_TOKEN && r.transactionId)
        await openPaddleCheckout(r.transactionId, () => {
          toast('¡Listo! Activando el plan del equipo…')
          setTimeout(() => void Promise.all([team.reload(), refreshUsage()]), 4000)
        })
      else if (r.url) location.assign(r.url)
      else toast('No se pudo abrir el pago. Intenta de nuevo.', 'error')
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="page narrow">
      <div className="page-head">
        <div className="grow">
          <h1>{t.nombre}</h1>
          <p className="muted">
            Tu rol: {ROL[t.miRol]} · {t.miembros.length}{' '}
            {t.miembros.length === 1 ? 'persona' : 'personas'} de {t.asientos}{' '}
            {t.plan === 'equipo' ? 'asientos' : 'en el equipo de prueba'}
          </p>
        </div>
      </div>
      <div className="stack">
        <section className="card stack" aria-label="Miembros">
          <h2>Miembros</h2>
          <ul className="stack" style={{ listStyle: 'none', margin: 0, padding: 0, gap: 8 }}>
            {t.miembros.map((m) => {
              const soyYo = m.userId === me?.user.id
              const puedeQuitar =
                !soyYo &&
                m.rol !== 'owner' &&
                (t.miRol === 'owner' || (t.miRol === 'admin' && m.rol === 'member'))
              return (
                <li key={m.userId} className="row wrap" style={{ gap: 8 }}>
                  <span className="grow">
                    {m.nombre ?? m.email}
                    {m.nombre && <span className="faint"> · {m.email}</span>}
                    {soyYo && <span className="faint"> (tú)</span>}
                    <span className="faint" style={{ fontSize: 12 }}>
                      {' '}
                      · desde {formatDate(m.desde)}
                    </span>
                  </span>
                  {t.miRol === 'owner' && m.rol !== 'owner' ? (
                    <select
                      aria-label={`Rol de ${m.email}`}
                      value={m.rol}
                      disabled={busy}
                      onChange={(e) =>
                        void act(
                          () => api.setMemberRole(m.userId, e.target.value as 'admin' | 'member'),
                          'Rol actualizado'
                        )
                      }
                    >
                      <option value="member">Miembro</option>
                      <option value="admin">Administrador</option>
                    </select>
                  ) : (
                    <span className="pill">{ROL[m.rol] ?? m.rol}</span>
                  )}
                  {t.miRol === 'owner' && !soyYo && (
                    <button
                      className="btn sm ghost"
                      disabled={busy}
                      onClick={() => {
                        if (
                          confirm(
                            `¿Pasar el equipo a ${m.email}? Quedarás como administrador y no podrás deshacerlo tú.`
                          )
                        )
                          void act(
                            () => api.transferOwnership(m.userId),
                            'Ahora el equipo tiene nuevo dueño'
                          )
                      }}
                    >
                      Hacer dueño
                    </button>
                  )}
                  {puedeQuitar && (
                    <button
                      className="btn sm ghost danger"
                      disabled={busy}
                      onClick={() => {
                        if (
                          confirm(
                            `¿Quitar a ${m.email}? Sus reuniones compartidas se quedan en el equipo; sus reuniones privadas se borran.`
                          )
                        )
                          void act(
                            () => api.removeMember(m.userId),
                            `${m.email} ya no está en el equipo`
                          )
                      }}
                    >
                      Quitar
                    </button>
                  )}
                </li>
              )
            })}
          </ul>
          <p className="faint" style={{ fontSize: 12, margin: 0 }}>
            Cada reunión es privada de quien la grabó hasta que la comparta con el equipo o la ponga
            en una carpeta del equipo. Si alguien sale, sus reuniones compartidas se quedan y las
            privadas se borran.
          </p>
        </section>

        {admin && (
          <section className="card stack" aria-label="Invitar">
            <h2>Invitar</h2>
            <form
              className="row wrap"
              onSubmit={(e) => {
                e.preventDefault()
                void invite()
              }}
            >
              <input
                className="grow"
                type="email"
                aria-label="Correo de la persona"
                placeholder="correo@empresa.cl"
                value={email}
                maxLength={200}
                onChange={(e) => setEmail(e.target.value)}
                disabled={lleno}
              />
              {t.miRol === 'owner' && (
                <select
                  aria-label="Rol"
                  value={rol}
                  onChange={(e) => setRol(e.target.value as 'member' | 'admin')}
                >
                  <option value="member">Miembro</option>
                  <option value="admin">Administrador</option>
                </select>
              )}
              <button className="btn primary" disabled={busy || lleno || !email.trim()}>
                Invitar
              </button>
            </form>
            {lleno && (
              <p className="notice warn" style={{ margin: 0 }}>
                {t.plan === 'equipo'
                  ? 'Usaste todos los asientos. Agrega asientos desde la facturación para invitar a más personas.'
                  : 'El equipo de prueba llegó a su tope. Contrata el plan Equipo para sumar más personas.'}
              </p>
            )}
            {t.invitaciones.length > 0 && (
              <>
                <h3 style={{ fontSize: 13, margin: '6px 0 0' }}>Pendientes</h3>
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }} className="stack">
                  {t.invitaciones.map((i) => (
                    <li key={i.id} className="row wrap" style={{ gap: 8 }}>
                      <span className="grow">
                        {i.email}{' '}
                        <span className="faint" style={{ fontSize: 12 }}>
                          · {ROL[i.rol]} · vence el {formatDate(i.expiraEn)}
                        </span>
                      </span>
                      <button
                        className="btn sm ghost"
                        disabled={busy}
                        onClick={() =>
                          void act(() => api.revokeInvitation(i.id), 'Invitación anulada')
                        }
                      >
                        Anular
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>
        )}

        {admin && (
          <section className="card stack" aria-label="Plan del equipo">
            <h2>Plan del equipo</h2>
            {t.plan === 'equipo' ? (
              <>
                <p style={{ margin: 0 }}>
                  Plan Equipo con {t.asientos} asientos
                  {usage ? ` · ${formatMinutes(usage.minutosRestantes)} disponibles este mes` : ''}.
                </p>
                <form
                  className="row wrap"
                  onSubmit={(e) => {
                    e.preventDefault()
                    void act(
                      () => api.changeSeats(Math.max(2, asientos)),
                      'Listo: el cambio de asientos se refleja en unos segundos'
                    )
                  }}
                >
                  <label className="row" style={{ gap: 6 }}>
                    <span className="faint">Cambiar a</span>
                    <input
                      type="number"
                      min={2}
                      max={200}
                      aria-label="Asientos"
                      value={asientos}
                      onChange={(e) => setAsientos(Number(e.target.value) || 2)}
                      style={{ width: 80 }}
                    />
                    <span className="faint">asientos</span>
                  </label>
                  <button className="btn" disabled={busy || asientos === t.asientos}>
                    Cambiar asientos
                  </button>
                  <span className="faint" style={{ fontSize: 12 }}>
                    Se cobra o abona la diferencia proporcional desde hoy.
                  </span>
                </form>
              </>
            ) : (
              <>
                <p className="muted" style={{ margin: 0 }}>
                  {clp(PLANS.equipo.precioClpMes ?? 0)} por persona al mes, IVA incluido (mínimo 2).
                  El equipo comparte {formatMinutes(PLANS.equipo.minutosIncluidos)} por persona. Si
                  no les sirve, les devolvemos el primer pago dentro de 14 días (
                  <a href={`${SITIO_URL}/terminos/`} target="_blank" rel="noreferrer">
                    términos
                  </a>
                  ).
                </p>
                <div className="row wrap">
                  <label className="row" style={{ gap: 6 }}>
                    <span className="faint">Asientos</span>
                    <input
                      type="number"
                      min={2}
                      max={200}
                      value={asientos}
                      onChange={(e) => setAsientos(Number(e.target.value) || 2)}
                      style={{ width: 80 }}
                    />
                  </label>
                  <span className="muted">
                    = {clp((PLANS.equipo.precioClpMes ?? 0) * Math.max(2, asientos))} al mes
                  </span>
                  <button className="btn primary" disabled={busy} onClick={() => void pay()}>
                    Contratar plan Equipo
                  </button>
                </div>
              </>
            )}
          </section>
        )}

        {t.miRol !== 'owner' && (
          <section className="card row wrap">
            <p className="grow muted" style={{ margin: 0 }}>
              Si sales del equipo dejas de ver sus reuniones. Tus reuniones compartidas se quedan;
              tus privadas se borran.
            </p>
            <button className="btn ghost danger" onClick={() => setLeaving(true)}>
              Salir del equipo
            </button>
          </section>
        )}
      </div>
      <Modal
        open={leaving}
        onClose={() => setLeaving(false)}
        title="¿Salir del equipo?"
        footer={
          <>
            <button className="btn ghost" onClick={() => setLeaving(false)}>
              Cancelar
            </button>
            <button className="btn live" disabled={busy} onClick={() => void leave()}>
              Salir
            </button>
          </>
        }
      >
        <p>
          Dejarás de ver las reuniones de «{t.nombre}». Tus reuniones compartidas se quedan en el
          equipo y tus reuniones privadas se borran. Para volver, alguien del equipo tendrá que
          invitarte de nuevo.
        </p>
      </Modal>
    </div>
  )
}
